import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Bell, ArrowLeft, Edit, Save, X, Plus, Trash2, 
  AlertCircle, CheckCircle, Info, MessageSquare 
} from 'lucide-react';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

export default function Notifications({ onBack }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'info',
    is_active: true
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${BASE_URL}/api/notifications/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setNotifications(response.data.notifications);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      notify.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (notification) => {
    setEditingItem(notification.id);
    setEditData({
      title: notification.title,
      message: notification.message,
      type: notification.type,
      is_active: notification.is_active
    });
  };

  const handleSave = async (id) => {
    setSaving(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${BASE_URL}/api/notifications/${id}/`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setNotifications(notifications.map(notif => 
          notif.id === id ? { ...notif, ...editData } : notif
        ));
        setEditingItem(null);
        setEditData({});
        notify.success('Notification updated successfully');
      }
    } catch (err) {
      console.error('Error updating notification:', err);
      notify.error('Failed to update notification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.delete(`${BASE_URL}/api/notifications/${id}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setNotifications(notifications.filter(notif => notif.id !== id));
        notify.success('Notification deleted successfully');
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
      notify.error('Failed to delete notification');
    }
  };

  const handleAddNotification = async () => {
    if (!newNotification.title.trim() || !newNotification.message.trim()) {
      notify.error('Title and message are required');
      return;
    }

    setSaving(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${BASE_URL}/api/notifications/`,
        newNotification,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setNotifications([response.data.notification, ...notifications]);
        setNewNotification({
          title: '',
          message: '',
          type: 'info',
          is_active: true
        });
        setShowAddForm(false);
        notify.success('Notification created successfully');
      }
    } catch (err) {
      console.error('Error creating notification:', err);
      notify.error('Failed to create notification');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setEditData({});
    setShowAddForm(false);
    setNewNotification({
      title: '',
      message: '',
      type: 'info',
      is_active: true
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Info className="w-4 h-4 text-blue-600" />;
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      info: 'bg-blue-100 text-blue-700',
      success: 'bg-green-100 text-green-700',
      warning: 'bg-yellow-100 text-yellow-700',
      error: 'bg-red-100 text-red-700'
    };
    return badges[type] || badges.info;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Bell className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-800">Notification Management</h2>
                <p className="text-sm text-slate-600">Manage system notifications and alerts</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-medical-500 hover:bg-medical-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Notification
          </button>
        </div>

        {/* Add Notification Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Add New Notification</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                <input
                  type="text"
                  value={newNotification.title}
                  onChange={(e) => setNewNotification({...newNotification, title: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  placeholder="Notification title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                <select
                  value={newNotification.type}
                  onChange={(e) => setNewNotification({...newNotification, type: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                >
                  <option value="info">Info</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <textarea
                  value={newNotification.message}
                  onChange={(e) => setNewNotification({...newNotification, message: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  placeholder="Notification message"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={newNotification.is_active}
                  onChange={(e) => setNewNotification({...newNotification, is_active: e.target.checked})}
                  className="w-4 h-4 text-medical-600 bg-gray-100 border-gray-300 rounded focus:ring-medical-500"
                />
                <label htmlFor="is_active" className="ml-2 text-sm font-medium text-slate-700">
                  Active
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNotification}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-medical-500 hover:bg-medical-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Creating...' : 'Create Notification'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Notifications Table */}
        <div className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                    Title
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                    Message
                  </th>
                  {/* <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                    Status
                  </th> */}
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {notifications.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <MessageSquare className="w-12 h-12 text-slate-400" />
                        <p className="text-slate-500">No notifications found</p>
                        <button
                          onClick={() => setShowAddForm(true)}
                          className="text-medical-600 hover:text-medical-700 font-medium"
                        >
                          Create your first notification
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  notifications.map((notification) => (
                    <tr key={notification.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(notification.type)}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeBadge(notification.type)}`}>
                            {notification.type}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {editingItem === notification.id ? (
                          <input
                            type="text"
                            value={editData.title}
                            onChange={(e) => setEditData({...editData, title: e.target.value})}
                            className="w-full px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                          />
                        ) : (
                          <span className="font-medium text-slate-900">{notification.title}</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {editingItem === notification.id ? (
                          <textarea
                            value={editData.message}
                            onChange={(e) => setEditData({...editData, message: e.target.value})}
                            rows={2}
                            className="w-full px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                          />
                        ) : (
                          <span className="text-sm text-slate-600">{notification.message}</span>
                        )}
                      </td>
                      {/* <td className="px-6 py-4">
                        {editingItem === notification.id ? (
                          <select
                            value={editData.is_active}
                            onChange={(e) => setEditData({...editData, is_active: e.target.value === 'true'})}
                            className="px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            notification.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {notification.is_active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td> */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {editingItem === notification.id ? (
                            <>
                              <button
                                onClick={() => handleSave(notification.id)}
                                disabled={saving}
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                              >
                                <Save className="w-4 h-4" />
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={handleCancel}
                                disabled={saving}
                                className="flex items-center gap-1 px-3 py-1.5 bg-slate-500 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                              >
                                <X className="w-4 h-4" />
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(notification)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-medical-500 hover:bg-medical-600 text-white text-sm font-medium rounded-lg transition-colors"
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(notification.id)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">About Notifications</h4>
              <p className="text-sm text-blue-700">
                Manage system-wide notifications that appear to users. You can create different types of notifications 
                (info, success, warning, error) and control their active status. Only active notifications will be 
                displayed to users in the application.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}